import {
  classNeedsPlacings as scoreCommonClassNeedsPlacings,
  placingGroupsNeedingPlacings,
  placingGroupsOutOfDate,
} from "score-common";

/**
 * #2852 — THE single named predicate for "this class needs placings", used
 * by every surface that renders the tag so they can never disagree again:
 *
 * - the server's badge-counts route (#3463) for the ClassManager row badge +
 *   header count chip and the Placings nav badge in App.jsx (#3496: the FE reads
 *   that one call and no longer evaluates this itself for those badges);
 *   useClassesNeedingPlacings still feeds it each ride's judged status
 *   (batchRideStatus' derived status) as `status` for its non-badge uses;
 * - ClassPlacings' deriveClassSummary (picker chip + "Needs placings only"
 *   filter) — getClassResults rows already carry that same judged-status
 *   domain in their own `status` field.
 *
 * A class needs placings exactly when the operator can commit placings for
 * it AND placing hasn't started yet:
 *
 * 1. it has at least one non-SCRATCHED ride;
 * 2. EVERY non-SCRATCHED ride's judged status is FINAL — the same bar
 *    score-api's setClassPlacings / PUT /ride/:rideId/placing enforce
 *    (#2113, isPlaceableStatus): CONFIRMED is NOT enough anymore, an
 *    unsigned sheet can still be reopened, and a tag that invited placing
 *    a CONFIRMED class walked the operator into a 409. FINAL also fires on
 *    the deliberate terminal acts (admin force-final, active ride-level
 *    No Show/Excused), which is exactly right — those rides are placeable;
 * 3. NO non-SCRATCHED ride has a placing committed yet — "zero placed",
 *    NOT "at least one unplaced" (#2852 driver decision, superseding
 *    #1079's "some placed, one blank still badges" rule: a fully-FINAL
 *    class with one eliminated/no-show/excused ride reaches FINAL but that
 *    ride is never placed, #1079's rule badged it forever even once every
 *    placeable ride was placed; the tag now clears the moment the operator
 *    has committed any placing for the class, since placing is a
 *    whole-class action). placing 0 is a legitimate placing (#534), so
 *    only null/undefined counts as "not yet placed".
 *
 * SCRATCHED rides are excluded from ALL THREE checks: they never need
 * judging, so they never block all-FINAL (score-api's own placing gate
 * skips them too), and they never report a placing (#2840 stamps their
 * result placing null), so they must not masquerade as "already placed"
 * (or as "unplaced") on either side of the check.
 *
 * Ride state only — never the clock (#340): a class whose scheduled time
 * has passed but whose rides aren't FINAL isn't waiting on placings, it's
 * still being scored, so it must NOT qualify.
 *
 * #3464: in a class placed by division (Class.placedByDivision === true) the
 * three tests above run PER PLACING GROUP (one group per division, the same
 * grouping the auto-placer and the Place screen use), and the class needs
 * placings while ANY group does: it is still ONE class on the badge (#3509
 * narrowed that for a by-division class: it flags only once NO group still has a
 * non-FINAL counted ride, see score-common classNeedsPlacings). A class
 * that is not placed by division is one group, so it behaves exactly as above.
 * The rule itself lives in score-common (placing-groups.js) so card 3463's
 * server endpoint shares it.
 *
 * #3465: a placed group whose placings are OUT OF DATE (the current scores give a
 * different order) needs placings again, same badge. That check needs the rows'
 * scores, so it runs only when `outOfDate` is passed (getClassResults rows, via
 * outOfDateOptions); the ride-list callers that hold only status + placing omit
 * it and are unchanged.
 *
 * #3462: the SECOND class state, "Can't place yet" (score-common cant-place-yet.js), is
 * the complement for a group held open by an unfinished ride: a group with an
 * unfinished ride never needs placings (it is not all FINAL), so the two states
 * are exclusive PER GROUP. #3509: a by-division class with division A finished and
 * unplaced and division B holding a stuck ride shows Can't place yet only, not
 * Needs placings (the class flags once the WHOLE class can be placed); #3464's
 * batch-scoped Place gate still lets A be placed early.
 *
 * @param {{status?: string, placing?: number|null, riderStatus?: string|null}[]|null|undefined} rides the class's rides, each carrying its judged status (DERIVED_RIDE_STATUSES domain), placing and (for a placed-by-division class) riderStatus
 * @param {boolean} [placedByDivision] Class.placedByDivision
 * @param {{classType?: string, placesAwarded?: number|null}|null} [outOfDate] see outOfDateOptions
 * @returns {boolean} true exactly when the "Needs placings" tag should render
 */
export function classNeedsPlacings(rides, placedByDivision, outOfDate) {
  return scoreCommonClassNeedsPlacings(rides, placedByDivision, outOfDate ?? undefined);
}

/**
 * #3465: the inputs of the out-of-date placings check for a class, from its
 * getClassResults rows (each carries the resolved `placingClassType`) and its
 * Class. null when the rows do not carry the class type (an older server), so
 * the check is skipped rather than guessed.
 *
 * @returns {{classType: string, placesAwarded: number|null}|null}
 */
export function outOfDateOptions(rows, classItem) {
  const classType = (rows ?? []).find((r) => r?.placingClassType)?.placingClassType;
  if (!classType) return null;
  return { classType, placesAwarded: classItem?.placesAwarded ?? null };
}

/**
 * #3465: every ride whose place would change if the class were re-placed from
 * its current scores, across all placing groups, for the class page.
 *
 * @returns {{rideId: string, oldPlace: number|null, newPlace: number}[]}
 */
export function outOfDateMoves(rows, classItem) {
  const options = outOfDateOptions(rows, classItem);
  if (!options) return [];
  return placingGroupsOutOfDate(rows, classItem?.placedByDivision === true, options).flatMap((g) => g.moves);
}

/**
 * #3464: the divisions of a placed-by-division class that still need placings,
 * for the class page. `division` is the group's rider status, null for Open.
 * Empty for a class that is not placed by division (nothing to name: the whole
 * class is the one group).
 *
 * @returns {{key: string, division: string|null}[]}
 */
export function divisionsNeedingPlacings(rides, placedByDivision) {
  if (placedByDivision !== true) return [];
  return placingGroupsNeedingPlacings(rides, true).map(({ key, division }) => ({ key, division }));
}

export default classNeedsPlacings;
